import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { db, auth } from '../services/firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { useClub } from '../contexts/ClubContext';
import Navbar from '../components/Navbar';

function CoachDashboard() {
  const [user, authLoading] = useAuthState(auth);
  const { club, clubId: contextClubId } = useClub();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Fallback to URL param for Safari/slow devices
  const clubId = contextClubId || searchParams.get('club');
  
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [teamStats, setTeamStats] = useState({ totalPlayers: 0, avgAttendance: 0 });

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      const loginUrl = clubId ? `/login?club=${clubId}` : '/login';
      navigate(loginUrl);
    }
  }, [user, authLoading, navigate, clubId]);

  // Load coach data and team info
  useEffect(() => {
    const loadCoachData = async () => {
      if (!user || !clubId) return;
      
      setLoading(true);
      try {
        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          
          // Load players for coach's assigned team
          if (data.team) {
            const playersQuery = query(
              collection(db, 'players'),
              where('clubId', '==', clubId),
              where('team', '==', data.team)
            );
            const playersSnapshot = await getDocs(playersQuery);
            const players = playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeamPlayers(players);
            
            // Calculate team stats
            setTeamStats(prev => ({ ...prev, totalPlayers: players.length }));
          } else {
            // If no specific team assigned, show all players for this club
            const allPlayersQuery = query(
              collection(db, 'players'),
              where('clubId', '==', clubId)
            );
            const allPlayersSnapshot = await getDocs(allPlayersQuery);
            const allPlayers = allPlayersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeamPlayers(allPlayers);
            setTeamStats(prev => ({ ...prev, totalPlayers: allPlayers.length }));
          }
        }

        // Load upcoming events for this club
        const now = new Date();
        const eventsQuery = query(
          collection(db, 'events'),
          where('clubId', '==', clubId),
          where('date', '>=', now.toISOString().split('T')[0]),
          orderBy('date', 'asc'),
          limit(10)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        const events = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUpcomingEvents(events);

        // Load recent attendance records
        const attendanceQuery = query(
          collection(db, 'attendance'),
          where('clubId', '==', clubId),
          orderBy('date', 'desc'),
          limit(20)
        );
        const attendanceSnapshot = await getDocs(attendanceQuery);
        const attendance = attendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRecentAttendance(attendance);
        
        // Calculate average attendance
        if (attendance.length > 0) {
          const presentCount = attendance.filter(a => a.status === 'present').length;
          const avgAttendance = Math.round((presentCount / attendance.length) * 100);
          setTeamStats(prev => ({ ...prev, avgAttendance }));
        }

      } catch (error) {
        console.error('Error loading coach data:', error);
      }
      setLoading(false);
    };

    loadCoachData();
  }, [user, clubId]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate(clubId ? `/?club=${clubId}` : '/');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      training: '🏃',
      match: '⚽',
      meeting: '📋',
      tournament: '🏆'
    };
    return icons[type] || '📅';
  };

  if (authLoading || loading) {
    return (
      <div className="player-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="player-dashboard-page coach-dashboard-page">
      <Navbar />
      
      <div className="player-dashboard-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Coach Dashboard</h1>
            <p>Welcome, Coach {userData?.firstName || userData?.displayName || user?.email?.split('@')[0]}!</p>
            {userData?.team && <span className="coach-team-badge">{userData.team}</span>}
          </div>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>

        {/* Stats Overview */}
        <div className="coach-stats-row">
          <div className="coach-stat-card">
            <span className="stat-icon">👥</span>
            <div className="stat-info">
              <span className="stat-value">{teamStats.totalPlayers}</span>
              <span className="stat-label">Players</span>
            </div>
          </div>
          <div className="coach-stat-card">
            <span className="stat-icon">📅</span>
            <div className="stat-info">
              <span className="stat-value">{upcomingEvents.length}</span>
              <span className="stat-label">Upcoming Events</span>
            </div>
          </div>
          <div className="coach-stat-card">
            <span className="stat-icon">📊</span>
            <div className="stat-info">
              <span className="stat-value">{teamStats.avgAttendance}%</span>
              <span className="stat-label">Avg Attendance</span>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="dashboard-grid">
          {/* Team Roster Card */}
          <div className="dashboard-card team-roster-card">
            <div className="card-header">
              <h2>Team Roster {userData?.team ? `- ${userData.team}` : ''}</h2>
              <span className="player-count">{teamPlayers.length} players</span>
            </div>
            <div className="card-body">
              {teamPlayers.length > 0 ? (
                <div className="roster-list">
                  <table className="roster-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Position</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamPlayers.slice(0, 10).map(player => (
                        <tr key={player.id}>
                          <td><span className="player-number">{player.number || '-'}</span></td>
                          <td>{player.name}</td>
                          <td><span className="position-badge">{player.position || '-'}</span></td>
                          <td>
                            {player.phone && (
                              <a href={`tel:${player.phone}`} className="contact-link">
                                📞
                              </a>
                            )}
                            {player.email && (
                              <a href={`mailto:${player.email}`} className="contact-link">
                                📧
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {teamPlayers.length > 10 && (
                    <p className="more-players">+ {teamPlayers.length - 10} more players</p>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <span className="empty-icon">👥</span>
                  <p>No players assigned to your team yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Events Card */}
          <div className="dashboard-card events-card">
            <div className="card-header">
              <h2>Upcoming Events</h2>
              <Link to={`/schedule?club=${clubId}`} className="view-link">View Full Schedule</Link>
            </div>
            <div className="card-body">
              {upcomingEvents.length > 0 ? (
                <div className="events-list">
                  {upcomingEvents.slice(0, 5).map(event => (
                    <div key={event.id} className="event-item">
                      <div className="event-date-box">
                        <span className="event-day">{new Date(event.date).getDate()}</span>
                        <span className="event-month">{new Date(event.date).toLocaleString('default', { month: 'short' })}</span>
                      </div>
                      <div className="event-info">
                        <h4>{getEventTypeIcon(event.type)} {event.title}</h4>
                        <p className="event-meta">
                          <span className="event-time">{formatTime(event.time)}</span>
                          {event.location && <span className="event-location">@ {event.location}</span>}
                        </p>
                        {event.team && <span className="event-team-badge">{event.team}</span>}
                      </div>
                      {/* Take Attendance button for coaches */}
                      <Link 
                        to={`/admin/attendance/${event.id}?club=${clubId}`} 
                        className="btn-take-attendance"
                        title="Take Attendance"
                      >
                        📋
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-events">
                  <div className="no-events-icon">📅</div>
                  <p>No upcoming events scheduled</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Attendance Card */}
          <div className="dashboard-card attendance-card">
            <div className="card-header">
              <h2>Recent Attendance</h2>
            </div>
            <div className="card-body">
              {recentAttendance.length > 0 ? (
                <div className="attendance-list">
                  {/* Group by event */}
                  {upcomingEvents.length > 0 && (
                    <div className="attendance-summary">
                      <p>Last {Math.min(recentAttendance.length, 20)} attendance records</p>
                      <div className="attendance-breakdown">
                        <span className="present">
                          ✓ {recentAttendance.filter(a => a.status === 'present').length} present
                        </span>
                        <span className="absent">
                          ✗ {recentAttendance.filter(a => a.status === 'absent').length} absent
                        </span>
                        <span className="excused">
                          ⊘ {recentAttendance.filter(a => a.status === 'excused').length} excused
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-attendance">
                  <div className="no-attendance-icon">📋</div>
                  <p>No attendance records yet</p>
                  <p className="hint">Take attendance at events to track player participation</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="dashboard-card actions-card">
            <div className="card-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="card-body">
              <div className="quick-actions-grid">
                <Link to={`/schedule?club=${clubId}`} className="quick-action">
                  <span className="action-icon">📅</span>
                  <span>View Schedule</span>
                </Link>
                <Link to={`/teams?club=${clubId}`} className="quick-action">
                  <span className="action-icon">👥</span>
                  <span>View Teams</span>
                </Link>
                <Link to={`/gallery?club=${clubId}`} className="quick-action">
                  <span className="action-icon">📸</span>
                  <span>Photo Gallery</span>
                </Link>
                <Link to={`/contact?club=${clubId}`} className="quick-action">
                  <span className="action-icon">📞</span>
                  <span>Contact Club</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Club Info Card */}
          <div className="dashboard-card club-info-card">
            <div className="card-header">
              <h2>Club Information</h2>
            </div>
            <div className="card-body">
              <div className="club-details">
                <h3>{club?.name}</h3>
                <div className="club-info-row">
                  <span className="info-icon">📍</span>
                  <span>{club?.location || club?.address || 'Trinidad & Tobago'}</span>
                </div>
                {club?.email && (
                  <div className="club-info-row">
                    <span className="info-icon">📧</span>
                    <a href={`mailto:${club.email}`}>{club.email}</a>
                  </div>
                )}
                {club?.phone && (
                  <div className="club-info-row">
                    <span className="info-icon">📞</span>
                    <a href={`tel:${club.phone}`}>{club.phone}</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoachDashboard;
